import { ChevronDown20, Close20 } from '@carbon/icons-react';
import { Divider } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { FieldOption } from 'api/tasks';
import { Avatar } from 'components/atoms/avatar';
import { Checkbox } from 'components/atoms/checkbox';
import { CpBody01Placeholder } from 'components/atoms/customer-portal/typography';
import { DatePicker } from 'components/atoms/date-picker';
import { useGetAllUsers } from 'hooks/use-idb';
import { useIsResponsive } from 'hooks/use-media-query';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { CpInput, CpTextArea } from '../../cp-input';
import { CpMenu } from '../../modal/cp-menu';
import { CpPopover as Popover } from '../../modal/cp-popover';
import { CpColorTag } from '../../tags/cp-tags';
import {
  CpSelect,
  CpTagComponents,
  CpUserComponents,
  TagForUser,
} from './select-components';
import { CpTaskDrawerFieldProps } from './types';

const getPopoverHeight = (optionsLength = 0) => {
  return `${((optionsLength ?? 0) + 2.5) * 48}px`;
};

const CpPopover = styled(Popover)`
  @media (max-width: 480px) {
    padding-top: 0;
    ${CpMenu} {
      padding-top: 0;
    }
    ${CpMenu.Header} {
      padding-top: var(--cp-spacing-06);
    }
  }
`;

export const CpDatePicker = styled(DatePicker)`
  &.ant-picker {
    width: 100%;
    background: var(--cp-white);
    padding: 0 var(--cp-spacing-05);
    cursor: pointer;
    :hover {
      background: var(--cp-gray-10);
    }
  }

  input {
    font-size: var(--cp-body-01-font-size);
    color: var(--cp-text-primary);

    &::placeholder {
      color: var(--cp-text-placeholder);
    }
  }

  .ant-picker-clear {
    opacity: 1;
    background: transparent;

    svg {
      fill: var(--cp-text-primary);
    }
  }

  .ant-picker-suffix svg {
    fill: var(--cp-text-primary);
  }
`;

type ValueType = string | undefined | number;
type MultiSelectValueType = (string | number)[] | undefined;

export const Placeholder = styled.div<{
  noBorder?: boolean;
  focused?: boolean;
}>`
  height: 100%;
  width: 100%;
  max-width: 501px;

  display: flex;
  align-items: center;
  justify-content: start;
  border-bottom: ${(props) =>
    props?.noBorder ? 'none' : '1px solid var(--cp-border-strong)'};

  ${CpBody01Placeholder} {
    padding-left: var(--cp-spacing-06);
  }

  ${CpColorTag} {
    margin-right: var(--cp-spacing-02);
  }

  ${CpColorTag}:first-child {
    margin-left: var(--cp-spacing-05);
  }

  @media (min-width: 486px) {
    &:hover {
      background: var(--cp-gray-10);
    }
  }

  ${(props) =>
    props?.focused
      ? css`
          background: var(--cp-gray-10);
          outline: 2px solid var(--cp-blue-60);
          border-bottom: none;
        `
      : ''};
`;

const InputInsidePopoverWrapper = styled.div`
  padding: var(--cp-spacing-05);
  ${CpInput} {
    border: 2px solid var(--cp-blue-60);
  }
`;

const StyledMenu = styled(CpMenu)`
  ${CpMenu.Header} {
    position: sticky;
    top: 0;
    z-index: 100;
    background: var(--cp-white);
    width: 100%;
  }

  ${InputInsidePopoverWrapper} {
    position: sticky;
    top: 40px;
    width: 100%;
    padding-top: var(--cp-spacing-03);

    background: var(--cp-white);
    z-index: 100;
  }
`;

export const InputLikeDiv = styled.div.attrs({
  className: 'cp-select-input-field',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--cp-border-strong);
  min-height: 42px;
  width: 100%;
  max-width: 501px;
  over ${CpBody01Placeholder} {
    padding-left: var(--cp-spacing-06);
  }

  ${CpColorTag} {
    margin-right: var(--cp-spacing-02);
  }

  @media (min-width: 486px) {
    &:hover {
      background: var(--cp-gray-10);
    }
  }

  .ant-divider {
    height: 26px;
    margin: 0;
  }
`;

export const ActionsWrapperInputLike = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const TagsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: var(--cp-spacing-02);
`;

const ActionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  cursor: pointer;
`;

const DropdownActionIconWrapper = styled(ActionIconWrapper).attrs({
  className: 'cp-select-input-dropdown-indicator',
})``;
const ActionSeperator = styled(Divider).attrs({
  className: 'cp-select-input-indicator-seperator',
  type: 'vertical',
})``;

export const StyledCheckbox = styled(Checkbox)`
  display: flex;
  margin-left: var(--cp-spacing-05);

  @media (max-width: 480px) {
    margin-left: var(--cp-spacing-02);
  }

  .ant-checkbox-inner {
    border: 2px solid var(--cp-gray-100);
    border-radius: 2px;
  }

  ${Placeholder}:has(&) {
    height: 42px;
  }
`;

export const CpTdSingleSelectField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const isResponsive = useIsResponsive();
  const [state, setState] = useState<ValueType>(field?.value as ValueType);
  const translate = useTranslate();
  const [search, setSearch] = useState<string>('');

  const onSearchChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback((event) => {
      setSearch(event.target.value);
    }, []);

  const selectedOption = useMemo(() => {
    return field?.metaData?.options?.find?.((option) => option.value === state);
  }, [field?.metaData?.options, state]);

  useEffect(() => {
    if (_.isEqual(field?.value, state)) {
      return;
    }
    setState(field?.value as ValueType);
  }, [field?.value, state]);

  if (isResponsive) {
    return (
      <CpPopover
        height={getPopoverHeight(field?.metaData?.options?.length)}
        closeOnSelect
        content={
          <>
            {/* <StickyContainer>
              <StyledMenu.Header>
                <FormattedMessage id="select" />
              </StyledMenu.Header>
              <InputInsidePopoverWrapper>
                <CpInput
                  placeholder={translate('search')}
                  value={search}
                  onChange={onSearchChange}
                />
              </InputInsidePopoverWrapper>
            </StickyContainer> */}
            <StyledMenu selectedKeys={state ? [state?.toString()] : []}>
              <StyledMenu.Header>
                <FormattedMessage id="select" />
              </StyledMenu.Header>
              <InputInsidePopoverWrapper
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <CpInput
                  placeholder={translate('search')}
                  value={search}
                  onChange={onSearchChange}
                />
              </InputInsidePopoverWrapper>
              {field?.metaData?.options?.map((option) => {
                if (
                  !!search?.trim() &&
                  !option.label.toLowerCase().includes(search.toLowerCase())
                ) {
                  return <></>;
                }

                return (
                  <StyledMenu.Item
                    key={option.value}
                    onClick={() => {
                      setState(option.value);
                      onChange(option.value);
                      setSearch('');
                    }}
                  >
                    <CpColorTag
                      color={option?.color ?? getRandomColor(option?.label)}
                    >
                      {option.label}
                    </CpColorTag>
                  </StyledMenu.Item>
                );
              })}
            </StyledMenu>
          </>
        }
      >
        <InputLikeDiv>
          {selectedOption ? (
            <div>
              <CpColorTag
                color={
                  selectedOption?.color ?? getRandomColor(selectedOption?.label)
                }
              >
                {selectedOption?.label}
              </CpColorTag>
            </div>
          ) : (
            <CpBody01Placeholder>{translate('select')}</CpBody01Placeholder>
          )}
          <ActionsWrapperInputLike>
            {selectedOption ? (
              <ActionIconWrapper
                onClick={(event) => {
                  event.stopPropagation();
                  setState(undefined);
                  onChange(null);
                }}
              >
                <Close20 />
              </ActionIconWrapper>
            ) : (
              <></>
            )}
            <ActionSeperator />
            <DropdownActionIconWrapper>
              <ChevronDown20 />
            </DropdownActionIconWrapper>
          </ActionsWrapperInputLike>
        </InputLikeDiv>
      </CpPopover>
    );
  }

  return (
    <CpSelect
      value={selectedOption}
      clearable
      isClearable
      options={field?.metaData?.options}
      onChange={(option: FieldOption<number> | null) => {
        setState(option?.value);
        onChange(option?.value);
      }}
      components={CpTagComponents}
    />
  );
};

export const CpTdMultiSelectField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const translate = useTranslate();
  const isResponsive = useIsResponsive();
  const [state, setState] = useState<MultiSelectValueType>(
    field?.value as MultiSelectValueType,
  );
  const [search, setSearch] = useState<string>('');

  const LIMIT = 1;

  const selectedOptions = useMemo(() => {
    const so = field?.metaData?.options?.filter?.((option) =>
      state?.includes?.(option.value),
    );

    if (!so) {
      return [];
    }

    return so;
  }, [field?.metaData?.options, state]);

  const selectedOptionsForUi = useMemo(() => {
    if (selectedOptions?.length > LIMIT) {
      return selectedOptions.slice(0, LIMIT).concat({
        label: translate('editor.plus_n_more', {
          count: selectedOptions.length - LIMIT,
        }),
        value: 'more',
        color: 'blue',
      } as unknown as FieldOption<number>);
    }

    return selectedOptions;
  }, [selectedOptions, translate]);

  if (isResponsive) {
    return (
      <CpPopover
        onClose={() => {
          onChange(state);
        }}
        height={getPopoverHeight(field?.metaData?.options?.length)}
        content={
          <StyledMenu selectedKeys={state?.map((s) => s?.toString()) ?? []}>
            <StyledMenu.Header>
              <FormattedMessage id="select" />
            </StyledMenu.Header>
            <InputInsidePopoverWrapper>
              <CpInput
                placeholder={translate('search')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </InputInsidePopoverWrapper>
            {field?.metaData?.options?.map((option) => {
              if (
                !!search?.trim() &&
                !option.label.toLowerCase().includes(search.toLowerCase())
              ) {
                return <></>;
              }

              return (
                <StyledMenu.Item
                  key={option.value}
                  onClick={() => {
                    if (state?.includes(option.value)) {
                      setState(state?.filter((s) => s !== option.value));
                    } else {
                      setState([...(state ?? []), option.value]);
                    }
                  }}
                >
                  <CpColorTag
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    {option.label}
                  </CpColorTag>
                </StyledMenu.Item>
              );
            })}
          </StyledMenu>
        }
      >
        <InputLikeDiv>
          {selectedOptionsForUi?.length ? (
            <TagsWrapper>
              {selectedOptionsForUi?.map((option) => {
                return (
                  <CpColorTag
                    key={option.value}
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    {option.label}
                  </CpColorTag>
                );
              })}
            </TagsWrapper>
          ) : (
            <CpBody01Placeholder>{translate('select')}</CpBody01Placeholder>
          )}
          <ActionsWrapperInputLike>
            {selectedOptions?.length ? (
              <ActionIconWrapper
                onClick={(event) => {
                  event.stopPropagation();
                  setState([]);
                  onChange([]);
                }}
              >
                <Close20 />
              </ActionIconWrapper>
            ) : (
              <></>
            )}
            <ActionSeperator />
            <DropdownActionIconWrapper>
              <ChevronDown20 />
            </DropdownActionIconWrapper>
          </ActionsWrapperInputLike>
        </InputLikeDiv>
      </CpPopover>
    );
  }

  return (
    <CpSelect
      isClearable
      value={selectedOptions}
      options={field?.metaData?.options}
      onChange={(options: FieldOption<number | 'more'>[]) => {
        const selectedValues = options
          ?.map((o) => o.value as number | 'more')
          .filter((v) => v !== 'more');

        setState(selectedValues);
      }}
      closeMenuOnSelect={false}
      clearable
      isMulti
      onMenuClose={() => {
        onChange(state);
      }}
      components={CpTagComponents}
    />
  );
};

export const CpTdTextField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const translate = useTranslate();
  const [state, setState] = useState<ValueType>(field?.value as ValueType);

  return (
    <CpInput
      placeholder={translate('customer_portal.input.text.placeholder')}
      value={state}
      onChange={(event) => {
        setState(event.target.value);
      }}
      onBlur={() => {
        onChange(state);
      }}
    />
  );
};

export const CpTdNumberField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const [state, setState] = useState<ValueType>(field?.value as ValueType);
  const placeholder = useTranslation('enter_a_value');

  return (
    <CpInput
      value={state}
      placeholder={placeholder}
      type="number"
      onChange={(event) => {
        setState(event.target.valueAsNumber);
      }}
      onBlur={() => {
        onChange(state);
      }}
    />
  );
};

export const CpTdMultiLineTextField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const [state, setState] = useState<ValueType>(field?.value as ValueType);
  const translate = useTranslate();

  return (
    <CpTextArea
      placeholder={translate('customer_portal.input.text.placeholder')}
      value={state}
      onChange={(event) => {
        setState(event.target.value);
      }}
      onBlur={() => {
        onChange(state);
      }}
    />
  );
};

export const CpTdBooleanField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  return (
    <Placeholder
      noBorder
      onClick={() => {
        onChange(!field?.value);
      }}
    >
      <StyledCheckbox checked={!!field?.value} value={!!field?.value} />
    </Placeholder>
  );
};

export const CpTdDateField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const translate = useTranslate();

  return (
    <CpDatePicker
      getPopupContainer={() =>
        document.querySelector('#cp-modal-root') as HTMLElement
      }
      clearIcon={<Close20 />}
      suffixIcon={null}
      placeholder={translate('select')}
      allowClear
      value={field?.value ? moment(field?.value) : undefined}
      onChange={(date) => onChange(date?.format('YYYY-MM-DD'))}
    />
  );
};

export const CpTdSingleUserField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const [state, setState] = useState<ValueType>(field?.value as ValueType);
  const users = useGetAllUsers({
    includeDeactivatedUsers: false,
  });
  const translate = useTranslate();
  const isResponsive = useIsResponsive();
  const [search, setSearch] = useState<string>('');

  const options = useMemo(() => {
    return users?.map((user) => {
      const fn = fullName(user);

      return {
        label: fn,
        value: user?.userId,
        color: getRandomColor(fn),
        user,
      };
    });
  }, [users]);

  const selectedOption = useMemo(() => {
    return options?.find?.((option) => option.value === state);
  }, [options, state]);

  if (isResponsive) {
    return (
      <CpPopover
        closeOnSelect
        height={getPopoverHeight(options?.length)}
        content={
          <StyledMenu selectedKeys={state ? [state?.toString()] : []}>
            <CpMenu.Header>
              <FormattedMessage id="select" />
            </CpMenu.Header>
            <InputInsidePopoverWrapper
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <CpInput
                placeholder={translate('search')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </InputInsidePopoverWrapper>
            {options?.map((option) => {
              if (
                !!search?.trim() &&
                !option.label.toLowerCase().includes(search.toLowerCase())
              ) {
                return <></>;
              }

              return (
                <CpMenu.Item
                  key={option.value}
                  onClick={() => {
                    setState(option.value);
                    onChange(option.value);
                    setSearch('');
                  }}
                >
                  <TagForUser
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    <Avatar
                      size="small"
                      name={option?.label}
                      src={option?.user?.photoUrl}
                    />
                    {option.label}
                  </TagForUser>
                </CpMenu.Item>
              );
            })}
          </StyledMenu>
        }
      >
        <InputLikeDiv>
          {selectedOption ? (
            <TagForUser
              color={
                selectedOption?.color ?? getRandomColor(selectedOption?.label)
              }
            >
              <Avatar
                size="small"
                name={selectedOption?.label}
                src={selectedOption?.user?.photoUrl}
              />
              {selectedOption.label}
            </TagForUser>
          ) : (
            <CpBody01Placeholder>{translate('select')}</CpBody01Placeholder>
          )}
          <ActionsWrapperInputLike>
            {selectedOption ? (
              <ActionIconWrapper
                onClick={(event) => {
                  event.stopPropagation();
                  setState(undefined);
                  onChange(null);
                }}
              >
                <Close20 />
              </ActionIconWrapper>
            ) : (
              <></>
            )}
            <ActionSeperator />
            <DropdownActionIconWrapper>
              <ChevronDown20 />
            </DropdownActionIconWrapper>
          </ActionsWrapperInputLike>
        </InputLikeDiv>
      </CpPopover>
    );
  }

  return (
    <CpSelect
      value={selectedOption}
      clearable
      isClearable
      options={options}
      onChange={(change: FieldOption<number> | null) => {
        setState(change?.value);
        onChange(change?.value);
      }}
      components={CpUserComponents}
    />
  );
};

export const CpTdMultiUserField: React.FC<CpTaskDrawerFieldProps> = ({
  field,
  onChange,
}) => {
  const [state, setState] = useState<MultiSelectValueType>(
    field?.value as MultiSelectValueType,
  );
  const users = useGetAllUsers({
    includeDeactivatedUsers: false,
  });
  const [search, setSearch] = useState<string>('');
  const translate = useTranslate();
  const isResponsive = useIsResponsive();

  const options = useMemo(() => {
    return users?.map((user) => {
      const fn = fullName(user);

      return {
        label: fn,
        value: user?.userId,
        color: getRandomColor(fn),
        user,
      };
    });
  }, [users]);

  const selectedOptions = useMemo(() => {
    const so = options?.filter?.((option) => state?.includes?.(option.value));

    if (!so) {
      return [];
    }

    return so;
  }, [options, state]);

  const selectedOptionsForUi = useMemo(() => {
    const LIMIT = 1;

    if (selectedOptions?.length > LIMIT) {
      return selectedOptions.slice(0, LIMIT).concat({
        label: translate('editor.plus_n_more', {
          count: selectedOptions.length - LIMIT,
        }),
        value: 'more',
        color: 'blue',
      } as unknown as any);
    }

    return selectedOptions;
  }, [selectedOptions, translate]);

  if (isResponsive) {
    return (
      <CpPopover
        onClose={() => {
          onChange(state);
        }}
        height={getPopoverHeight(options?.length)}
        content={
          <StyledMenu selectedKeys={state?.map((s) => s?.toString()) ?? []}>
            <StyledMenu.Header>
              <FormattedMessage id="select" />
            </StyledMenu.Header>
            <InputInsidePopoverWrapper>
              <CpInput
                placeholder={translate('search')}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />
            </InputInsidePopoverWrapper>
            {options?.map((option) => {
              if (
                !!search?.trim() &&
                !option.label.toLowerCase().includes(search.toLowerCase())
              ) {
                return <></>;
              }

              return (
                <CpMenu.Item
                  key={option.value}
                  onClick={() => {
                    if (state?.includes(option.value)) {
                      setState(state?.filter((s) => s !== option.value));
                    } else {
                      setState([...(state ?? []), option.value]);
                    }
                  }}
                >
                  <TagForUser
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    <Avatar
                      size="small"
                      name={option?.label}
                      src={option?.user?.photoUrl}
                    />
                    {option.label}
                  </TagForUser>
                </CpMenu.Item>
              );
            })}
          </StyledMenu>
        }
      >
        <InputLikeDiv>
          {selectedOptionsForUi?.length ? (
            <TagsWrapper>
              {selectedOptionsForUi?.map((option) =>
                (option?.value as unknown as string) === 'more' ? (
                  <CpColorTag
                    key={option.value}
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    {option.label}
                  </CpColorTag>
                ) : (
                  <TagForUser
                    color={option?.color ?? getRandomColor(option?.label)}
                  >
                    <Avatar
                      size="small"
                      name={option?.label}
                      src={option?.user?.photoUrl}
                    />
                    {option.label}
                  </TagForUser>
                ),
              )}
            </TagsWrapper>
          ) : (
            <CpBody01Placeholder>{translate('select')}</CpBody01Placeholder>
          )}
          <ActionsWrapperInputLike>
            {selectedOptions?.length ? (
              <ActionIconWrapper
                onClick={(event) => {
                  event.stopPropagation();
                  setState([]);
                  onChange([]);
                }}
              >
                <Close20 />
              </ActionIconWrapper>
            ) : (
              <></>
            )}
            <ActionSeperator />
            <DropdownActionIconWrapper>
              <ChevronDown20 />
            </DropdownActionIconWrapper>
          </ActionsWrapperInputLike>
        </InputLikeDiv>
      </CpPopover>
    );
  }

  return (
    <CpSelect
      value={selectedOptions}
      options={options}
      onChange={(change: FieldOption<number | 'more'>[]) => {
        const selectedValues = change
          ?.map((o) => o.value as number | 'more')
          .filter((v) => v !== 'more');

        setState(selectedValues);
      }}
      closeMenuOnSelect={false}
      clearable
      isClearable
      isMulti
      onMenuClose={() => {
        onChange(state);
      }}
      components={CpUserComponents}
    />
  );
};
